import React from 'react';
import {
    MapPinIcon,
    PhoneIcon,
    GlobeAltIcon
} from '@heroicons/react/24/outline';
import LanguageSwitcher from '../LanguageSwitcher';

const PublicAgencyHeader = ({ agency, t, i18n }) => {
    return (
        <div className="bg-hero-luxury border-b border-gold/10 shadow-lg relative overflow-hidden">
            {/* Language Toggle Button */}
            <div className={`absolute top-4 z-20 ${i18n.language.startsWith('ar') ? 'left-4' : 'right-4'}`}>
                <LanguageSwitcher align={i18n.language.startsWith('ar') ? 'left' : 'right'} />
            </div>

            {/* Background Accents */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-gold/5 rounded-full blur-3xl pointer-events-none -translate-y-1/2 translate-x-1/2"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-gold/5 rounded-full blur-3xl pointer-events-none translate-y-1/2 -translate-x-1/2"></div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 relative z-10">
                <div className="flex flex-col md:flex-row items-center md:items-start gap-8">
                    {/* Logo */}
                    <div className="flex-shrink-0">
                        {agency.logo ? (
                            <img
                                src={`${import.meta.env.VITE_API_URL || ''}/static/agency_logos/${agency.logo}`}
                                alt={`${i18n.language === 'ar' ? (agency.name_ar || agency.name) : (agency.name_en || agency.name)} Logo`}
                                className="h-32 w-32 sm:h-48 sm:w-48 object-contain"
                            />
                        ) : (
                            <div className="h-32 w-32 sm:h-48 sm:w-48 bg-transparent border border-gold/20 rounded-2xl flex items-center justify-center text-4xl sm:text-6xl font-bold text-gold shadow-xl">
                                {(i18n.language === 'ar' ? (agency.name_ar || agency.name) : (agency.name_en || agency.name)).charAt(0)}
                            </div>
                        )}
                    </div>

                    {/* Info */}
                    <div className={`flex-grow text-center ${i18n.language === 'ar' ? 'md:text-right' : 'md:text-left'} text-white`}>
                        <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 text-white drop-shadow-md">{i18n.language === 'ar' ? (agency.name_ar || agency.name) : (agency.name_en || agency.name)}</h1>
                        {agency.description && (
                            <p className="text-gray-200 text-lg mb-6 max-w-2xl leading-relaxed">{agency.description}</p>
                        )}

                        <div className="flex flex-wrap justify-center md:justify-start gap-4 text-sm items-center">
                            {agency.phone && (
                                <a
                                    href={`https://wa.me/${agency.phone.replace(/[^0-9]/g, '')}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 bg-[#25D366] hover:bg-[#128C7E] text-white px-5 py-2.5 rounded-full font-bold shadow-lg transform hover:-translate-y-0.5 transition-all text-sm sm:text-base mr-2"
                                >
                                    <svg className="h-5 w-5 fill-current" viewBox="0 0 24 24">
                                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.008-.57-.008-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                                    </svg>
                                    WhatsApp
                                </a>
                            )}
                            {agency.city && (
                                <div className="flex items-center gap-2 bg-surface-glass border border-gold/10 backdrop-blur-md px-3 py-2 rounded-lg text-white/90">
                                    <MapPinIcon className="h-5 w-5 text-gold" />
                                    <span>{agency.city}{agency.address ? `, ${agency.address}` : ''}</span>
                                </div>
                            )}
                            {agency.phone && (
                                <div className="flex items-center gap-2 bg-surface-glass border border-gold/10 backdrop-blur-md px-3 py-2 rounded-lg text-white/90">
                                    <PhoneIcon className="h-5 w-5 text-gold" />
                                    <span dir="ltr">{agency.phone}</span>
                                </div>
                            )}
                            {agency.website && (
                                <a href={agency.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 bg-surface-glass border border-gold/10 backdrop-blur-md px-3 py-2 rounded-lg hover:bg-gold/10 hover:text-white hover:border-gold/30 transition-all text-white/90">
                                    <GlobeAltIcon className="h-5 w-5 text-gold" />
                                    <span>{t('website')}</span>
                                </a>
                            )}
                            {agency.facebook && (
                                <a href={agency.facebook} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 bg-surface-glass border border-gold/10 backdrop-blur-md px-3 py-2 rounded-lg hover:bg-gold/10 hover:text-white hover:border-gold/30 transition-all text-white/90">
                                    <svg className="h-5 w-5 fill-current text-gold" viewBox="0 0 24 24">
                                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                                    </svg>
                                    <span>{t('facebook')}</span>
                                </a>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PublicAgencyHeader;
